import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  MessageSquare,
  Download,
  RefreshCw,
  Search,
  Calendar,
  Phone,
  ArrowLeft,
  ArrowUpRight,
  ArrowDownLeft,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Filter,
  BarChart3,
  Server
} from "lucide-react";

export default function AdminWhatsAppLogs() {
  const navigate = useNavigate();
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [phoneFilter, setPhoneFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("all");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [environment, setEnvironment] = useState("preview"); // preview or prod
  
  // Data state
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(50);
  const [fetchingLogs, setFetchingLogs] = useState(false);
  const [fetchingStats, setFetchingStats] = useState(false);
  
  // Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/admin/auth/me`, {
          credentials: "include"
        });
        
        if (response.ok) {
          const data = await response.json();
          const role = data.admin?.role || data.role;
          if (role === "super_admin" || role === "admin") {
            setIsAuthenticated(true);
          } else {
            toast.error("Access denied. Super admin required.");
            navigate("/admin");
          }
        } else {
          navigate("/admin");
        }
      } catch {
        navigate("/admin");
      }
      setLoading(false);
    };
    
    checkAuth();
  }, [navigate]);

  // Get base API URL based on environment
  const getApiUrl = useCallback(() => {
    if (environment === "prod") {
      return "https://eduzy.co/api";
    }
    return API; // Preview API
  }, [environment]);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setFetchingLogs(true);
    
    try {
      const params = new URLSearchParams();
      
      if (phoneFilter) params.append("phone", phoneFilter);
      if (channelFilter !== "all") params.append("channel", channelFilter);
      if (directionFilter !== "all") params.append("direction", directionFilter);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      params.append("limit", pageSize.toString());
      params.append("offset", (currentPage * pageSize).toString());
      
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/whatsapp/message-logs?${params}`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setTotalCount(data.total_count || 0);
      } else {
        toast.error("Failed to fetch logs");
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
    
    setFetchingLogs(false);
  }, [phoneFilter, channelFilter, directionFilter, startDate, endDate, currentPage, pageSize, getApiUrl]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setFetchingStats(true);
    
    try {
      const apiUrl = getApiUrl();
      const channel = channelFilter !== "all" ? channelFilter : "";
      const response = await fetch(
        `${apiUrl}/whatsapp/message-stats?hours=24${channel ? `&channel=${channel}` : ""}`,
        { credentials: "include" }
      );
      
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch {
      // Stats are optional
    }
    
    setFetchingStats(false);
  }, [channelFilter, getApiUrl]);

  // Initial fetch
  useEffect(() => {
    if (isAuthenticated) {
      fetchLogs();
      fetchStats();
    }
  }, [isAuthenticated, fetchLogs, fetchStats]);

  // Handle search
  const handleSearch = () => {
    setCurrentPage(0);
    fetchLogs();
    fetchStats();
  };

  // Handle export
  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      
      if (phoneFilter) params.append("phone", phoneFilter);
      if (channelFilter !== "all") params.append("channel", channelFilter);
      if (directionFilter !== "all") params.append("direction", directionFilter);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      params.append("format", format);
      
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/whatsapp/message-logs/export?${params}`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `whatsapp_logs_${environment}_${new Date().toISOString().slice(0, 10)}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(`Exported ${format.toUpperCase()} successfully`);
      } else {
        toast.error("Export failed");
      }
    } catch (err) {
      toast.error(`Export error: ${err.message}`);
    }
  };

  // Format timestamp
  const formatTime = (isoString) => {
    if (!isoString) return "-";
    try {
      const date = new Date(isoString);
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      });
    } catch {
      return isoString;
    }
  };

  // Format delay
  const formatDelay = (seconds) => {
    if (seconds === null || seconds === undefined) return "-";
    if (seconds < 1) return "<1s";
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6" data-testid="whatsapp-logs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-emerald-600" />
          <h1 className="text-xl font-bold text-gray-900">WhatsApp Message Logs</h1>
        </div>
        {/* Environment Selector */}
        <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setEnvironment("preview")}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              environment === "preview"
                ? "bg-white shadow text-emerald-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <Server className="w-4 h-4 inline mr-1" />
            Preview
          </button>
          <button
            onClick={() => setEnvironment("prod")}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              environment === "prod"
                ? "bg-white shadow text-blue-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <Server className="w-4 h-4 inline mr-1" />
            Production
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <ArrowDownLeft className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Incoming (24h)</p>
                    <p className="text-2xl font-bold">{stats.incoming?.count || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ArrowUpRight className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Outgoing (24h)</p>
                    <p className="text-2xl font-bold">{stats.outgoing?.count || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Avg Processing</p>
                    <p className="text-2xl font-bold">
                      {formatDelay(stats.incoming?.avg_processing_time)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Avg Delivery Delay</p>
                    <p className="text-2xl font-bold">
                      {formatDelay(stats.incoming?.avg_delivery_delay)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
              {/* Phone Number */}
              <div className="lg:col-span-2">
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Enter phone number..."
                    value={phoneFilter}
                    onChange={(e) => setPhoneFilter(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              {/* Channel */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Channel
                </label>
                <Select value={channelFilter} onValueChange={setChannelFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Channels</SelectItem>
                    <SelectItem value="test">Test</SelectItem>
                    <SelectItem value="prod">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Direction */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Direction
                </label>
                <Select value={directionFilter} onValueChange={setDirectionFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="incoming">Incoming</SelectItem>
                    <SelectItem value="outgoing">Outgoing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Start Date */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              
              {/* End Date */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  End Date
                </label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-3 mt-4">
              <Button onClick={handleSearch} disabled={fetchingLogs}>
                {fetchingLogs ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Search
              </Button>
              
              <Button
                variant="outline"
                onClick={() => {
                  fetchLogs();
                  fetchStats();
                }}
                disabled={fetchingLogs}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${fetchingLogs ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              
              <div className="flex-1" />
              
              <Button
                variant="outline"
                onClick={() => handleExport("csv")}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              
              <Button
                variant="outline"
                onClick={() => handleExport("json")}
              >
                <Download className="w-4 h-4 mr-2" />
                Export JSON
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Message Logs</CardTitle>
                <CardDescription>
                  {totalCount > 0 
                    ? `Showing ${currentPage * pageSize + 1}-${Math.min((currentPage + 1) * pageSize, totalCount)} of ${totalCount} logs`
                    : "No logs found"
                  }
                </CardDescription>
              </div>
              <Badge variant={environment === "prod" ? "default" : "secondary"}>
                {environment === "prod" ? "Production" : "Preview"} Environment
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-[100px]">Direction</TableHead>
                    <TableHead className="w-[80px]">Channel</TableHead>
                    <TableHead className="w-[120px]">Phone</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead className="w-[150px]">Received</TableHead>
                    <TableHead className="w-[150px]">Responded</TableHead>
                    <TableHead className="w-[100px]">Processing</TableHead>
                    <TableHead className="w-[80px]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        {fetchingLogs ? (
                          <div className="flex items-center justify-center gap-2">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Loading logs...
                          </div>
                        ) : (
                          "No logs found. Try adjusting your filters."
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log, idx) => (
                      <TableRow key={idx} className="hover:bg-gray-50">
                        <TableCell>
                          {log.direction === "incoming" ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                              <ArrowDownLeft className="w-3 h-3 mr-1" />
                              In
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              <ArrowUpRight className="w-3 h-3 mr-1" />
                              Out
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline"
                            className={log.channel === "prod" 
                              ? "bg-purple-50 text-purple-700 border-purple-200" 
                              : "bg-gray-50 text-gray-700 border-gray-200"
                            }
                          >
                            {log.channel || "?"}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          ...{log.phone_suffix || log.phone_number?.slice(-4) || "????"}
                        </TableCell>
                        <TableCell className="max-w-[300px]">
                          <p className="truncate text-sm" title={log.message_text}>
                            {log.message_text || "-"}
                          </p>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {formatTime(log.received_at || log.sent_at || log.created_at)}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {formatTime(log.responded_at)}
                        </TableCell>
                        <TableCell>
                          {log.processing_time_seconds !== null && log.processing_time_seconds !== undefined ? (
                            <span className={`text-sm font-medium ${
                              log.processing_time_seconds > 5 ? "text-amber-600" : "text-green-600"
                            }`}>
                              {formatDelay(log.processing_time_seconds)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {log.status === "responded" || log.status === "sent" ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : log.status === "error" ? (
                            <XCircle className="w-4 h-4 text-red-500" />
                          ) : (
                            <Clock className="w-4 h-4 text-amber-500" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-500">
                  Page {currentPage + 1} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
    </div>
  );
}
